/* eslint-disable jsx-a11y/anchor-is-valid */
import React, { FunctionComponent, useCallback, useState } from 'react';
import { IMeetingRowProps } from './types';
import { useHistory } from 'react-router-dom';
import { useSelect, useSelectWithParams } from '../../../../../utils/selector.utils';
import { UserAvatars } from '../UserAvatars';
import { useDispatch } from 'react-redux';
import { openDialog } from '../../../../../app/dialog/dialog.actions';

import slugify from 'react-slugify';
import { convertAndFormatDateToUserTimeZone } from '../../../../../utils/date.utils';
import { meetingSimpleSelectors } from '../../../../../app/resource/meeting-simple/meeting-simple.selectors';
import {
  deleteMeetingSimple,
  updateMeetingSimple,
} from '../../../../../app/page/page-storage/meetings-simple/meetings-simple.actions';
import { IMeetingSimpleUpdate } from '../../../../../app/resource/meeting-simple/meeting-simple.types';
import { initialMeetingDashboardPage } from '../../../../../app/page/meeting-dashboard/meeting-dashboard.actions';
import { authSelectors } from '../../../../../app/auth/auth.selectors';
import { userSelectors } from '../../../../../app/resource/user/user.selectors';
import { CircularAvatar } from '../../../common/CustomAvatar';
import { isMemberAnAdmin } from '../../../../../utils/user.utils';
import { IUser } from '../../../../../app/resource/user/user.types';
import { isMemberPartOfMeeting } from '../../../../../utils/user.utils';

export const MeetingRow: FunctionComponent<IMeetingRowProps> = ({ meetingId }) => {
  const history = useHistory();

  const userId = useSelect(authSelectors.selectLoggedUser) as IUser['id'];
  const user = useSelectWithParams(userSelectors.selectResourceById, userId) as IUser;
  const meeting = useSelectWithParams(meetingSimpleSelectors.selectResourceById, meetingId);

  // Determinate if logged user is a meeting member for handle actions
  const isMeetingMember = user && meeting ? isMemberPartOfMeeting(user, meeting) : false;

  const dispatch = useDispatch();
  // const agendaItems = useSelectWithParams(
  //   agendaItemSelectors.selectResourcesById,
  //   meeting ? meeting.agendaItemIds : [],
  // );
  const openMeetingForm = useCallback(() => {
    if (meeting) {
      dispatch(
        openDialog('meetingFormDialog', {
          type: meeting.type,
          data: meeting,
          onSubmit: (meetingInfo: IMeetingSimpleUpdate) => {
            dispatch(
              updateMeetingSimple(meeting.id, {
                ...meetingInfo,
                type: meeting.type,
              }),
            );
          },
        }),
      );
    }
  }, [dispatch, meeting]);
  // const sessionsIds = meeting ? meeting.nextSessionIds : [];
  // const nextSessions = useSelectWithParams(sessionSelectors.selectResourcesById, sessionsIds);

  const onDeleteMeeting = useCallback(() => {
    if (meeting) {
      dispatch(deleteMeetingSimple(meeting.id));
    }
  }, [dispatch, meeting]);

  const openAgendaFormDialog = useCallback(() => {
    dispatch(openDialog('agendaFormDialog', { meetingId }));
  }, [dispatch, meetingId]);

  const goToKpi = useCallback(() => {
    if (meeting) {
      history.push(`/kpi/${meeting.id}`);
    }
  }, [meeting, history]);

  const meetingShare = meeting?.shareWithIds ? meeting.shareWithIds : [];
  const meetingAdmins = meeting?.administratorIds ? meeting.administratorIds : [];

  const meetingRowMembers = Array.from(new Set([...meetingShare, ...meetingAdmins]));

  const [isActive, setActive] = useState(false);

  const toggleClass = () => {
    setActive(!isActive);
  };
  const close = useCallback(() => setActive(false), [setActive]);

  // calculate the next session date-time to display
  let nextSessionDisplayDateTime =
    meeting?.nextSession &&
    user &&
    convertAndFormatDateToUserTimeZone(meeting?.nextSession, user?.timezone, 'MM/DD/YYYY dddd, hh:mm A');
  // if no next session it could be because the next session is next year the it session itself is not generated yet
  if (!nextSessionDisplayDateTime && meeting?.repeatType === 'years') {
    nextSessionDisplayDateTime = '(Next year)';
  }

  const getAgendaItemsForMeeting = useCallback(() => {
    //dispatch(getAgendaItems(meetingId));
    dispatch(initialMeetingDashboardPage(meetingId));
  }, [dispatch, meetingId]);

  return (
    meeting && (
      <div
        className="flex-table"
        role="rowgroup"
        onClick={() =>
          history.push(
            `/meeting-dashboard/${meeting.id}/${slugify(meeting.title, {
              delimiter: '-',
            })}`,
          )
        }
        style={{ cursor: 'pointer' }}
      >
        <div className="flex-row first" role="cell">
          <div className="account-item">
            <div
              style={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
              }}
            >
              <CircularAvatar
                src={meeting.image}
                title={meeting.title}
                size="50"
                color={meeting.color}
                textSizeRatio={3}
                classes="account-item-image f-2 bold"
              />
              <div className="account-item-desc">
                <h3 className="account-item-title">{meeting.title}</h3>
              </div>
            </div>
          </div>
        </div>
        <div className="flex-row" role="cell" style={{ display: 'flex', alignItems: 'center' }}>
          <UserAvatars userIds={meetingRowMembers} />
        </div>
        <div className="flex-row" role="cell">
          <div className="flex aic h100">{nextSessionDisplayDateTime}</div>
        </div>
        <div className="flex-row" role="cell">
          <div className="flex aic h100 justify-between">
            {isMeetingMember ? <span className="btn btn-md darkGray-bg mr-1">Start Meeting</span> : <span></span>}
            <div className="flex">
              {isMemberAnAdmin(user, meeting) && (
                <div
                  className="round-icon-small mr-1"
                  onClick={(e: React.MouseEvent<HTMLElement>) => {
                    openMeetingForm();
                    e.stopPropagation();
                  }}
                >
                  <i className="icon-pencil"></i>
                </div>
              )}
              {isMeetingMember ? (
                <>
                  <div
                    className="round-icon-small mr-1"
                    onClick={(e: React.MouseEvent<HTMLElement>) => {
                      openAgendaFormDialog();
                      e.stopPropagation();
                    }}
                    onMouseEnter={(e: React.MouseEvent<HTMLElement>) => {
                      getAgendaItemsForMeeting();
                      e.stopPropagation();
                    }}
                  >
                    AG
                  </div>
                  <div
                    onClick={(e: React.MouseEvent<HTMLElement>) => {
                      goToKpi();
                      e.stopPropagation();
                    }}
                    className="round-icon-small mr-1"
                  >
                    KPI
                  </div>
                </>
              ) : null}
              <div className={isActive ? 'dropdown opened' : 'dropdown'}>
                <div
                  className="round-icon-small"
                  onClick={(e: React.MouseEvent<HTMLElement>) => {
                    toggleClass();
                    e.stopPropagation();
                  }}
                >
                  X
                </div>
                <div
                  className="dropdown-menu drop-right drop-up max-h320"
                  style={{ bottom: '-40%' }}
                  onMouseLeave={(e: React.MouseEvent<HTMLElement>) => {
                    close();
                    e.stopPropagation();
                  }}
                >
                  <div className="mini-popup">
                    {/* <div className="mini-popup-header">
                    Delete meeting
                    <span
                      className="close_mini_popup"
                      onClick={(e: React.MouseEvent<HTMLElement>) => {
                        close();
                        e.stopPropagation();
                      }}
                    >
                      ×
                    </span>
                  </div> */}
                    <div className="mini-popup-body">
                      <p className="ln mb-2 darkGray">
                        Deleting a meeting is permanent and there is no way to get it back.
                      </p>
                      <a
                        className="btn btn-md red-bg white d-block jcc"
                        onClick={(e: React.MouseEvent<HTMLElement>) => {
                          onDeleteMeeting();
                          e.stopPropagation();
                        }}
                      >
                        Delete meeting
                      </a>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    )
  );
};
